import React from 'react';

import { array, constant, invariant, tagged } from '@code-expert/prelude';
import { Logger } from '/imports/modules/Logger/client';
import { useForceUpdateWithDep } from '/imports/ui/hooks/useForceUpdate';

type State = tagged.Tagged<'running', NodeJS.Timeout> | tagged.Tagged<'paused'>;

const State = tagged.build<State>();

type Action = tagged.Tagged<'set', [VoidFunction, number]> | tagged.Tagged<'clear'>;

const Action = tagged.build<Action>();

const paused = constant(State.wide.paused());
const update: (action: Action) => (current: State) => State = Action.fold({
  set: ([fn, interval]) =>
    State.fold({
      running: (id) => {
        clearInterval(id);
        return State.running(setInterval(fn, interval));
      },
      paused: () => State.running(setInterval(fn, interval)),
    }),
  clear: () =>
    State.fold({
      running: (id) => {
        clearInterval(id);
        return paused();
      },
      paused,
    }),
});

// TODO remove once Firefox 137 drops out of our support matrix
const captureStackTraceShim = (object: unknown) => {
  (object as { stack?: string }).stack = new Error().stack?.split('\n').slice(1).join('\n');
};

/**
 * Wraps {@link setInterval}. Triggers the function {@link fn} every {@link interval} milliseconds.
 * @param fn function to call
 * @param interval delay in milliseconds
 * @param run if {@link fn} should run or not
 */
export default function useInterval(fn: () => void, interval: number, run = true) {
  const { current } = React.useRef({
    dispatch: (a: Action) => {
      const prev = current.state;
      const next = (current.state = update(a)(current.state));
      current.debug.lastStateTransition = { next, prev };
    },
    state: paused(),
    fn,
    isMounted: false,
    run: (timeout: NodeJS.Timeout | undefined) => {
      if (
        !State.is.running(current.state) ||
        current.state.value !== timeout ||
        !current.isMounted
      ) {
        current.debug.locationCrash = window.location.href;
        current.debug.crashingTimeout = [timeout, new Date().toISOString()];
        current.debug.isMounted = current.isMounted;
        current.debug.crashingState = current.state;
        Logger.persistAlways.error('Expected useInterval to be in State "running"', current.debug);
      } else {
        current.fn();
      }
    },
    debug: {
      locationInitial: window.location.href,
      locationCrash: '',
      intervals: array.zero<[NodeJS.Timeout, string]>(),
      crashingTimeout: undefined as [NodeJS.Timeout | undefined, string] | undefined,
      isMounted: false,
      crashingState: undefined as State | undefined,
      lastStateTransition: undefined as { prev: State; next: State } | undefined,
    },
  });

  if (!('stack' in current.debug)) {
    if (typeof Error.captureStackTrace === 'function') Error.captureStackTrace(current.debug);
    else captureStackTraceShim(current.debug);
  }

  current.fn = fn;

  React.useEffect(() => {
    current.isMounted = true;
    return () => {
      current.isMounted = false;
    };
  }, [current]);

  React.useEffect(() => {
    if (run && current.isMounted) {
      let timeout: NodeJS.Timeout | undefined = undefined;
      current.dispatch(Action.set([() => current.run(timeout), interval]));
      invariant(State.is.running(current.state), 'Expected state "running" after "set" Action');
      timeout = current.state.value;
      current.debug.intervals.push([timeout, new Date().toISOString()]);
    }
    return () => current.dispatch(Action.clear());
  }, [current, interval, run]);
}

export function useIntervalTrigger(intervalDelay: number, run = true) {
  const [dependency, update] = useForceUpdateWithDep();
  useInterval(update, intervalDelay, run);
  return dependency;
}
